/**
 * Job Board — Auth utilities.
 * Stores JWT + role in localStorage under jb_ keys.
 * useJBAuth() hook provides reactive auth state.
 */
import { useState, useEffect, useCallback } from 'react';
import { jbApi } from './api';

const KEY_TOKEN = 'jb_token';
const KEY_ROLE  = 'jb_role';
const KEY_USER  = 'jb_user';

export const getToken = ()       => localStorage.getItem(KEY_TOKEN);
export const getRole  = ()       => localStorage.getItem(KEY_ROLE);

export const saveAuth = (token, role, user) => {
  localStorage.setItem(KEY_TOKEN, token);
  localStorage.setItem(KEY_ROLE, role);
  localStorage.setItem(KEY_USER, JSON.stringify(user));
};

export const clearAuth = () => {
  localStorage.removeItem(KEY_TOKEN);
  localStorage.removeItem(KEY_ROLE);
  localStorage.removeItem(KEY_USER);
};

export const getCachedUser = () => {
  try { return JSON.parse(localStorage.getItem(KEY_USER) || 'null'); }
  catch { return null; }
};

/**
 * Hook — returns { user, token, role, loading, login, logout, refresh }
 */
export function useJBAuth() {
  const [token, setToken]   = useState(() => getToken());
  const [role, setRole]     = useState(() => getRole());
  const [user, setUser]     = useState(() => getCachedUser());
  const [loading, setLoading] = useState(false);

  const login = useCallback((accessToken, userRole, userProfile) => {
    saveAuth(accessToken, userRole, userProfile);
    setToken(accessToken);
    setRole(userRole);
    setUser(userProfile);
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setToken(null);
    setRole(null);
    setUser(null);
  }, []);

  // Validate token on mount
  useEffect(() => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    setLoading(true);
    jbApi.me(t)
      .then(data => {
        login(t, data.role, data.profile);
      })
      .catch(() => {
        clearAuth();
        setToken(null); setRole(null); setUser(null);
      })
      .finally(() => setLoading(false));
  }, [login]);

  return { user, token, role, loading, login, logout };
}
